import { useEffect, useState } from "react";
import { ShieldCheck, UserPlus, Pencil, Ban, CheckCircle2, KeyRound, Copy, AlertTriangle, Crown, Users2, History, Search } from "lucide-react";
import { api, apiError, fmtDate, fmtDateTime } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ROLE_TONE = {
  SUPER_ADMIN: "bg-amber-100 text-amber-700", ADMIN: "bg-indigo-100 text-indigo-700",
  OPERATIONS: "bg-sky-100 text-sky-700", SUPPORT: "bg-teal-100 text-teal-700",
  VIEW_ONLY: "bg-slate-100 text-slate-600", LIMITED: "bg-violet-100 text-violet-700",
};
const PERM_ROLES = ["VIEW_ONLY", "LIMITED", "ADMIN", "OPERATIONS", "SUPPORT"];

function Chip({ children, tone = "bg-slate-100 text-slate-600" }) {
  return <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{children}</span>;
}

function PermPicker({ catalog, value, onChange }) {
  const groups = {};
  (catalog || []).forEach((p) => { const g = p.split(".")[0]; (groups[g] = groups[g] || []).push(p); });
  const toggle = (p) => onChange(value.includes(p) ? value.filter((x) => x !== p) : [...value, p]);
  return (
    <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2" data-testid="perm-picker">
      {Object.entries(groups).map(([g, perms]) => (
        <div key={g} className="mb-1">
          <p className="px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">{g.replace(/_/g, " ")}</p>
          <div className="grid grid-cols-2 gap-1">
            {perms.map((p) => (
              <label key={p} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50">
                <input type="checkbox" checked={value.includes(p)} onChange={() => toggle(p)} data-testid={`perm-${p}`} className="accent-[#14B8A6]" />
                <span>{p.split(".")[1]}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TempPasswordDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Temporary password</DialogTitle>
          <DialogDescription>Share this with <b>{data.email}</b> securely. It is shown only once and will be required to change on first login.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <code className="text-base font-bold text-[#0B132B]" data-testid="temp-password-value">{data.temp_password}</code>
          <button onClick={() => { navigator.clipboard?.writeText(data.temp_password); toast.success("Copied"); }}
            className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200" data-testid="temp-password-copy">
            <Copy size={13} /> Copy
          </button>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg bg-[#0B132B] px-4 py-2 text-sm font-semibold text-white" data-testid="temp-password-done">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminForm({ open, onClose, catalog, editing, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("VIEW_ONLY");
  const [perms, setPerms] = useState([]);
  const [status, setStatus] = useState("Active");
  const [busy, setBusy] = useState(false);
  const [confirmSuper, setConfirmSuper] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setName(editing.name || ""); setEmail(editing.email || ""); setRole(editing.role);
        setPerms(editing.explicit_permissions || editing.permissions || []); setStatus(editing.status || "Active");
      } else { setName(""); setEmail(""); setRole("VIEW_ONLY"); setPerms(catalog?.role_defaults?.VIEW_ONLY || []); setStatus("Active"); }
      setConfirmSuper(false);
    }
  }, [open, editing, catalog]);

  const onRole = (r) => { setRole(r); if (catalog?.role_defaults?.[r]) setPerms(catalog.role_defaults[r]); };
  const showPerms = PERM_ROLES.includes(role);
  const isSuperPick = role === "SUPER_ADMIN";

  const save = async () => {
    if (!editing && (!name.trim() || !email.trim())) return toast.error("Name and email are required");
    if (isSuperPick && !confirmSuper) return toast.error("Please confirm the Super Admin action");
    setBusy(true);
    try {
      if (editing) {
        const body = { name, role, status };
        if (!isSuperPick) body.permissions = showPerms ? perms : undefined;
        const { data } = await api.patch(`/admin/admins/${editing.id}`, body);
        toast.success("Admin updated");
        onSaved(null, data);
      } else {
        const body = { name, email, role, status };
        if (!isSuperPick && showPerms) body.permissions = perms;
        const { data } = await api.post("/admin/admins", body);
        toast.success("Admin created");
        onSaved(data, null);
      }
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="admin-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit admin" : "Create admin"}</DialogTitle>
          <DialogDescription>{editing ? `Update ${editing.email}` : "A one-time temporary password will be generated."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-form-name"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Email</label>
              <input value={email} disabled={!!editing} onChange={(e) => setEmail(e.target.value)} data-testid="admin-form-email"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6] disabled:bg-slate-50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Role</label>
              <Select value={role} onValueChange={onRole}>
                <SelectTrigger className="mt-1" data-testid="admin-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog?.roles || []).map((r) => <SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{r.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1" data-testid="admin-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {isSuperPick && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="super-confirm">
              <input type="checkbox" checked={confirmSuper} onChange={(e) => setConfirmSuper(e.target.checked)} className="mt-0.5 accent-amber-600" />
              <span><Crown size={12} className="mr-1 inline" /><b>Super Admin</b> has full access including admin management. Max 2 active Super Admins allowed. Confirm you intend to grant full control.</span>
            </label>
          )}
          {showPerms && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Permissions {role === "VIEW_ONLY" && <span className="text-slate-400">(read-only defaults)</span>}</label>
              <PermPicker catalog={catalog?.permissions} value={perms} onChange={setPerms} />
            </div>
          )}
          {!showPerms && !isSuperPick && (
            <p className="rounded-lg bg-slate-50 p-2 text-xs text-slate-500">This role uses its standard default permissions.</p>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="admin-form-save"
            className="rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f9c8c] disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_LABELS = {
  ADMIN_CREATE: "Admin created", ADMIN_UPDATE: "Admin edited",
  ADMIN_DISABLE: "Admin disabled", ADMIN_ENABLE: "Admin enabled",
  ADMIN_RESET_PASSWORD: "Password reset",
};
const ACTION_TONE = {
  ADMIN_CREATE: "bg-emerald-100 text-emerald-700", ADMIN_UPDATE: "bg-indigo-100 text-indigo-700",
  ADMIN_DISABLE: "bg-red-100 text-red-700", ADMIN_ENABLE: "bg-teal-100 text-teal-700",
  ADMIN_RESET_PASSWORD: "bg-amber-100 text-amber-700",
};

function activitySummary(r) {
  if (r.action === "ADMIN_CREATE") return `Role ${(r.after?.role || "").replace(/_/g, " ")}${r.after?.status === "Disabled" ? " (disabled)" : ""}`;
  if (r.action === "ADMIN_RESET_PASSWORD") return "Temporary password issued";
  if (r.action === "ADMIN_DISABLE") return "Status → Disabled";
  if (r.action === "ADMIN_ENABLE") return "Status → Active";
  if (r.action === "ADMIN_UPDATE") {
    const parts = [];
    if (r.after?.role) parts.push(`role → ${r.after.role.replace(/_/g, " ")}`);
    if (r.after?.status) parts.push(`status → ${r.after.status}`);
    if (r.after?.permissions) parts.push("permissions changed");
    return parts.join(", ") || "Updated";
  }
  return "—";
}

function AdminActivity() {
  const [data, setData] = useState(null);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = (off = 0) => {
    const p = new URLSearchParams({ limit: String(limit), offset: String(off) });
    if (actor.trim()) p.set("actor", actor.trim());
    if (action !== "all") p.set("action", action);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    api.get(`/admin/admin-activity?${p.toString()}`).then((r) => { setData(r.data); setOffset(off); })
      .catch((e) => { setData({ items: [], total: 0 }); toast.error(apiError(e)); });
  };
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="admin-activity-view">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="min-w-[160px] flex-1">
          <label className="text-[11px] font-semibold text-slate-500">Actor</label>
          <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="email…" data-testid="activity-actor"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-[#14B8A6]" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="activity-action"
            className="mt-1 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-[#14B8A6]">
            <option value="all">All</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="activity-from"
            className="mt-1 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-[#14B8A6]" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="activity-to"
            className="mt-1 block rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-[#14B8A6]" />
        </div>
        <button onClick={() => load(0)} data-testid="activity-apply"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B132B] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0B132B]/90">
          <Search size={14} /> Apply
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Date/time</th><th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">Summary</th><th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.items || []).map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`activity-row-${r.id}`}>
                <td className="px-4 py-3 text-slate-500">{fmtDateTime(r.timestamp)}</td>
                <td className="px-4 py-3 text-slate-700">{r.actor || "—"}</td>
                <td className="px-4 py-3"><Chip tone={ACTION_TONE[r.action]}>{ACTION_LABELS[r.action] || r.action}</Chip></td>
                <td className="px-4 py-3 text-slate-600">{r.target || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{activitySummary(r)}</td>
                <td className="px-4 py-3 text-slate-400">{r.ip || "—"}</td>
              </tr>
            ))}
            {data && (data.items || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No admin activity found for these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > limit && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>Showing {offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={offset === 0} onClick={() => load(offset - limit)} data-testid="activity-prev"
              className="rounded-lg border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40">Prev</button>
            <button disabled={offset + limit >= data.total} onClick={() => load(offset + limit)} data-testid="activity-next"
              className="rounded-lg border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TwoFAEmailDialog({ data, me, onClose, onSaved }) {
  const isSelf = data && me && data.id === me.id;
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) { setEmail(""); setOtp(""); setStep("email"); } }, [data]);
  if (!data) return null;

  const start = async () => {
    if (!email.trim()) return toast.error("Enter an email address");
    setBusy(true);
    try {
      if (isSelf) {
        const { data: r } = await api.post("/admin/2fa/change-email/start", { email });
        if (r.demo_otp) toast.message(`Preview code: ${r.demo_otp}`, { duration: 8000 });
        setStep("otp"); toast.success("Verification code sent to the new email");
      } else {
        await api.put(`/admin/admins/${data.id}/2fa-email`, { email });
        toast.success("2FA email set. This admin must verify it on next login.");
        onSaved(); onClose();
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/admin/2fa/change-email/verify", { code: otp });
      toast.success("Your 2FA email was updated");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent data-testid="twofa-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} /> {isSelf ? "Change my 2FA email" : `Set 2FA email for ${data.email}`}</DialogTitle>
          <DialogDescription>
            {isSelf
              ? "We'll send a code to the new email. It becomes active only after you verify it."
              : "The new email is stored unverified and this admin will be signed out. They must verify it on their next login before they can enter the console."}
          </DialogDescription>
        </DialogHeader>
        {step === "email" ? (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600">New 2FA email</label>
            <input data-testid="twofa-email-input" type="email" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-600">Enter the 6-digit code sent to {email}</label>
            <input data-testid="twofa-otp-input" inputMode="numeric" maxLength={6} className="w-full rounded-md border border-slate-200 px-3 py-2 text-center text-lg tracking-[0.4em]" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" />
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose} data-testid="twofa-cancel" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          {step === "email"
            ? <button data-testid="twofa-submit" onClick={start} disabled={busy} className="rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f9c8c] disabled:opacity-60">{isSelf ? "Send code" : "Save"}</button>
            : <button data-testid="twofa-verify" onClick={verify} disabled={busy || otp.length !== 6} className="rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f9c8c] disabled:opacity-60">Verify & update</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminManagement() {
  const [rows, setRows] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [me, setMe] = useState(null);
  const [twofa, setTwofa] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {type, admin}
  const [tab, setTab] = useState("admins");

  const load = () => {
    api.get("/admin/admins").then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(apiError(e)); });
    api.get("/admin/permissions-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/admin/whoami").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const doAction = async () => {
    const { type, admin } = confirmAction;
    try {
      if (type === "disable") { await api.post(`/admin/admins/${admin.id}/disable`); toast.success("Admin disabled"); }
      if (type === "enable") { await api.post(`/admin/admins/${admin.id}/enable`); toast.success("Admin enabled"); }
      if (type === "reset") { const { data } = await api.post(`/admin/admins/${admin.id}/reset-password`); setTempPw({ email: admin.email, temp_password: data.temp_password }); }
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setConfirmAction(null); }
  };

  if (rows === null) return <Skeleton className="h-96" />;
  const activeSuperCount = rows.filter((a) => a.role === "SUPER_ADMIN" && a.status !== "Disabled").length;

  return (
    <div className="space-y-5" data-testid="admin-management-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-[#0B132B]"><ShieldCheck size={22} /> Admin Management</h1>
          <p className="text-sm text-slate-500">Create and manage admin accounts, roles and granular permissions. Super Admin only.</p>
        </div>
        <button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="create-admin-btn"
          className={`inline-flex items-center gap-2 rounded-lg bg-[#14B8A6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0f9c8c] ${tab === "admins" ? "" : "hidden"}`}>
          <UserPlus size={16} /> Create Admin
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button onClick={() => setTab("admins")} data-testid="tab-admins"
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold ${tab === "admins" ? "border-b-2 border-[#14B8A6] text-[#0B132B]" : "text-slate-500"}`}>
          <Users2 size={15} /> Admins
        </button>
        <button onClick={() => setTab("activity")} data-testid="tab-activity"
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold ${tab === "activity" ? "border-b-2 border-[#14B8A6] text-[#0B132B]" : "text-slate-500"}`}>
          <History size={15} /> Admin Activity
        </button>
      </div>

      {tab === "activity" ? <AdminActivity /> : (
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">2FA</th><th className="px-4 py-3">Permissions</th><th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Last login</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50" data-testid={`admin-row-${a.email}`}>
                <td className="px-4 py-3 font-medium text-[#0B132B]">{a.name}{a.must_change_password && <span className="ml-2 text-[10px] text-amber-600">(pending pw change)</span>}</td>
                <td className="px-4 py-3 text-slate-600">{a.email}</td>
                <td className="px-4 py-3"><Chip tone={ROLE_TONE[a.role]}>{a.role.replace(/_/g, " ")}</Chip></td>
                <td className="px-4 py-3">
                  <Chip tone={a.status === "Disabled" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>{a.status}</Chip>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="text-xs leading-tight">
                      <div className="text-slate-600" data-testid={`twofa-email-${a.email}`}>{a.admin_2fa_email_masked || "—"}</div>
                      <Chip tone={a.admin_2fa_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{a.admin_2fa_verified ? "Verified" : "Not verified"}</Chip>
                    </div>
                    <button onClick={() => setTwofa(a)} title={me && a.id === me.id ? "Change my 2FA email" : "Set 2FA email"} data-testid={`twofa-${a.email}`}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#14B8A6]"><ShieldCheck size={15} /></button>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-500" title={(a.permissions || []).join(", ")}>
                  {a.role === "SUPER_ADMIN" ? "Full access" : `${(a.permissions || []).length} permission(s)`}
                </td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(a.created_at)}</td>
                <td className="px-4 py-3 text-slate-500">{a.last_login ? fmtDateTime(a.last_login) : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => { setEditing(a); setFormOpen(true); }} title="Edit" data-testid={`edit-${a.email}`}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#0B132B]"><Pencil size={15} /></button>
                    <button onClick={() => setConfirmAction({ type: "reset", admin: a })} title="Reset password" data-testid={`reset-${a.email}`}
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"><KeyRound size={15} /></button>
                    {a.status === "Disabled"
                      ? <button onClick={() => setConfirmAction({ type: "enable", admin: a })} title="Enable" data-testid={`enable-${a.email}`}
                          className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 size={15} /></button>
                      : (a.role === "SUPER_ADMIN" && activeSuperCount <= 1)
                        ? <button disabled title="The only active Super Admin cannot be disabled." data-testid={`disable-${a.email}`}
                            className="cursor-not-allowed rounded-md p-1.5 text-slate-300"><Ban size={15} /></button>
                        : <button onClick={() => setConfirmAction({ type: "disable", admin: a })} title="Disable" data-testid={`disable-${a.email}`}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-50"><Ban size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <AdminForm open={formOpen} onClose={() => setFormOpen(false)} catalog={catalog} editing={editing}
        onSaved={(created) => { load(); if (created?.temp_password) setTempPw({ email: created.email, temp_password: created.temp_password }); }} />
      <TempPasswordDialog data={tempPw} onClose={() => setTempPw(null)} />
      <TwoFAEmailDialog data={twofa} me={me} onClose={() => setTwofa(null)} onSaved={load} />

      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="admin-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" />
              {confirmAction?.type === "disable" && "Disable admin?"}
              {confirmAction?.type === "enable" && "Enable admin?"}
              {confirmAction?.type === "reset" && "Reset password?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "disable" && `${confirmAction?.admin?.email} will be logged out immediately and cannot sign in until re-enabled.`}
              {confirmAction?.type === "enable" && `${confirmAction?.admin?.email} will regain access with their existing permissions.`}
              {confirmAction?.type === "reset" && `A new one-time temporary password will be generated for ${confirmAction?.admin?.email}. Their current sessions will be invalidated.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doAction} data-testid="confirm-ok">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
